import { rem } from 'polished';
import styled, { css } from 'styled-components';

import { Text } from 'src/client/components';
import { BUTTON_2_2 } from 'src/client/constants/text';

import { EditLabel, OptionsGroup } from '../../styles';

type EditingElementsProps = {
  isEditing: boolean;
};

type ToggleOptionProps = {
  isActive: boolean;
};

export const LoadingContainer = styled.div`
  height: 400px;

  display: flex;
  justify-content: center;
  align-items: center;
`;

export const StyledEditLabel = styled(EditLabel)`
  position: absolute;
  top: -8px;
  left: calc(50% - 10px);
  transform: translateX(-50%);
  z-index: 2;
`;

export const GivesSection = styled.section`
  background: white;
`;

export const GivesSectionContentContainer = styled.div<EditingElementsProps>`
  margin: 0 auto;
  padding: 64px 20px 80px;
  max-width: 1600px;

  @media ${(props) => props.theme.screen.md} {
    padding: 64px 32px 80px;
  }

  @media ${(props) => props.theme.screen.xl} {
    padding: 64px 76px 80px;
  }
`;

export const CardsSectionContainer = styled.div<EditingElementsProps>`
  position: relative;

  border: ${(props) =>
    props.isEditing ? `1px solid ${props.theme.colors.border3}` : 'inherit'};
  border-radius: 6px;

  &:hover,
  &:focus {
    border: ${(props) =>
      props.isEditing ? `1px solid ${props.theme.colors.border3}` : 'inherit'};
    box-shadow: none;
  }
`;

export const CardsHeader = styled(Text)<EditingElementsProps>`
  position: relative;
  padding: 24px 0 0 24px;

  color: ${(props) => props.theme.colors.gray11};

  font-family: ${(props) => props.theme.fontFamilies.vollkorn};
  font-weight: ${(props) => props.theme.fontWeights.medium};
  font-size: 48px;
  line-height: 63px;
  letter-spacing: -0.96px;

  display: flex;
  align-items: center;
  justify-content: space-between;

  @media ${(props) => props.theme.screen.md} {
    padding: ${(props) =>
      props.isEditing ? '40px 0 0 39px' : '40px 0 32px 39px'};

    font-size: ${rem('96px')};
    line-height: ${rem('125px')};
    letter-spacing: -1.92px;
  }
`;

export const RecipientFilterHeader = styled.div<EditingElementsProps>`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 0 24px;

  ${(props) =>
    props.isEditing &&
    css`
      ${CardsHeader} {
        padding: 0 0 0 15px;
      }
    `}

  button {
    width: max-content;
    margin-bottom: 32px;
    padding: 8px 16px;
    display: flex;
    align-items: center;

    span {
      ${BUTTON_2_2}
    }

    span > svg {
      height: 16px;
      width: 16px;
    }
  }

  @media ${(props) => props.theme.screen.md} {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 32px;

    button {
      margin: 0;
    }
  }
`;

export const RecipientGalleryViewToggleGroup = styled.div<{
  isLoading: boolean;
}>`
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%) translateY(-50%);
  z-index: 10;

  color: ${(props) => props.theme.colors.white};
  background: ${(props) => props.theme.colors.gray9};
  width: fit-content;
  padding: 4px;

  display: flex;
  align-items: stretch;
  gap: 4px;

  border-radius: 50px;

  ${(props) =>
    props.isLoading &&
    css`
      * {
        cursor: progress !important;
      }
    `}
`;

export const IconContainer = styled.div<{ isActive: boolean; color: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  flex-shrink: 0;

  background: ${(props) =>
    props.isActive ? props.color : props.theme.colors.lightEmphasis3};

  border: ${(props) => (props.isActive ? '1px solid black' : 'none')};
  border-radius: 50%;
  box-shadow: ${(props) => (props.isActive ? '1px 2px' : 'none')};

  img {
    width: 20px;
    height: 20px;
    object-fit: contain;
  }

  .anticon {
    font-size: 16px;
    color: white;
  }
`;

export const ToggleOption = styled.div<ToggleOptionProps>`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  max-width: 280px;

  padding: 8px 12px;
  cursor: pointer;

  background: ${(props) =>
    props.isActive ? 'white' : props.theme.colors.gray9};
  color: ${(props) =>
    props.isActive ? 'black' : props.theme.colors.lightEmphasis2};

  border-radius: 50px;

  & > div > p:first-child {
    color: ${(props) => (props.isActive ? 'black' : 'white')};
  }

  & > div > p:last-child {
    font-size: 10px;
    line-height: 1.4;

    @media (max-width: 992px) {
      display: none;
    }
  }
`;

export { OptionsGroup };
